
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getStockService } from "../../services/stockService";
import { checkoutSaleService } from "../../services/salesService";
import { API_ORIGIN } from "../../services/httpService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";

type Product = {
    _id: string;
    productName: string;
    sellingPrice: number;
    quantity: number;
    imageUrl: string;
};

type CartItem = {
    productId: string;
    productName: string;
    unitPrice: number;
    quantity: number;
    imageUrl: string;
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const asNumber = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return 0;
};

const buildImageUrl = (url?: string): string => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return url.startsWith("/") ? `${API_ORIGIN}${url}` : `${API_ORIGIN}/${url}`;
};

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string } } };
    return errObj.response?.data?.message ?? fallback;
};

const SalesPage = () => {
    const { user } = useAuth();
    const canEditSales = hasPermission(user, "sales.write");

    const [products, setProducts] = useState<Product[]>([]);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [selectionQty, setSelectionQty] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchProducts = useCallback(async () => {
        if (!user?.id) return;

        setLoading(true);
        setError("");

        try {
            const response = await getStockService();
            const normalized = response
                .map((entry) => {
                    const product = asRecord(entry);
                    const imageObj = asRecord(product.image);
                    const imagePath =
                        asString(imageObj.url) ||
                        asString(product.imageUrl) ||
                        asString(product.productImage) ||
                        asString(product.image);

                    return {
                        _id: asString(product._id) || asString(product.id),
                        productName: asString(product.productName) || asString(product.name),
                        sellingPrice: asNumber(product.sellingPrice),
                        quantity: asNumber(product.quantity),
                        imageUrl: buildImageUrl(imagePath),
                    };
                })
                .filter((product) => product._id && product.productName);

            setProducts(normalized);
        } catch (err: unknown) {
            console.error("Failed to fetch products", err);
            setError(readErrorMessage(err, "Failed to load products"));
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const getSelectedQty = (productId: string) => selectionQty[productId] ?? 1;

    const adjustSelectedQty = (productId: string, nextQty: number) => {
        setSelectionQty((prev) => ({
            ...prev,
            [productId]: Math.max(1, nextQty),
        }));
    };

    const addToCart = (product: Product) => {
        const selectedQty = getSelectedQty(product._id);
        if (selectedQty > product.quantity) {
            showErrorToast("Not enough stock quantity available");
            return;
        }

        setCart((prev) => {
            const existing = prev.find((item) => item.productId === product._id);
            if (existing) {
                const updatedQty = existing.quantity + selectedQty;
                if (updatedQty > product.quantity) {
                    showErrorToast("Not enough stock quantity available");
                    return prev;
                }
                return prev.map((item) =>
                    item.productId === product._id ? { ...item, quantity: updatedQty } : item
                );
            }

            return [
                ...prev,
                {
                    productId: product._id,
                    productName: product.productName,
                    unitPrice: product.sellingPrice,
                    quantity: selectedQty,
                    imageUrl: product.imageUrl,
                },
            ];
        });
    };

    const updateCartQty = (productId: string, qty: number) => {
        const product = products.find((item) => item._id === productId);
        if (!product) return;

        if (qty <= 0) {
            setCart((prev) => prev.filter((item) => item.productId !== productId));
            return;
        }

        if (qty > product.quantity) {
            showErrorToast("Not enough stock quantity available");
            return;
        }

        setCart((prev) => prev.map((item) => (item.productId === productId ? { ...item, quantity: qty } : item)));
    };

    const removeCartItem = (productId: string) => {
        setCart((prev) => prev.filter((item) => item.productId !== productId));
    };

    const total = useMemo(
        () => cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
        [cart]
    );

    const checkout = async () => {
        if (!user?.id) {
            showErrorToast("User not logged in");
            return;
        }
        if (!cart.length) {
            showErrorToast("Cart is empty");
            return;
        }

        setCheckoutLoading(true);
        try {
            await checkoutSaleService({
                items: cart.map((item) => ({
                    productId: item.productId,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                })),
                total,
            });

            setCart([]);
            await fetchProducts();
            showSuccessToast("Checkout completed successfully");
        } catch (err: unknown) {
            console.error("Checkout failed", err);
            showErrorToast(readErrorMessage(err, "Checkout failed"));
        } finally {
            setCheckoutLoading(false);
        }
    };

    return (
        <div className="p-4 md:p-6">
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold">Sales</h1>
            </div>

            {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
                <section>
                    {loading ? (
                        <p className="text-gray-500">Loading products...</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {products.map((product) => {
                                const selectedQty = getSelectedQty(product._id);
                                return (
                                    <div key={product._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                        <div className="h-36 w-full overflow-hidden rounded-lg bg-gray-100 mb-3 flex items-center justify-center">
                                            {product.imageUrl ? (
                                                <img src={product.imageUrl} alt={product.productName} className="h-full w-full object-cover" />
                                            ) : (
                                                <span className="text-sm text-gray-400">No image</span>
                                            )}
                                        </div>

                                        <h2 className="text-lg font-semibold text-[#7f0d09]">{product.productName}</h2>
                                        <p className="text-2xl font-bold mt-2">${product.sellingPrice.toFixed(2)}</p>
                                        <p className="text-sm text-gray-500 mt-1">In stock: {product.quantity}</p>

                                        {canEditSales && (
                                            <>
                                                <div className="mt-4 flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => adjustSelectedQty(product._id, selectedQty - 1)}
                                                        className="h-9 w-9 rounded-md bg-amber-500 text-black font-bold"
                                                    >
                                                        -
                                                    </button>
                                                    <div className="h-9 min-w-12 rounded-md border border-gray-300 flex items-center justify-center px-2">
                                                        {selectedQty}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => adjustSelectedQty(product._id, selectedQty + 1)}
                                                        className="h-9 w-9 rounded-md bg-amber-500 text-black font-bold"
                                                    >
                                                        +
                                                    </button>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => addToCart(product)}
                                                    className="mt-4 w-full rounded-md bg-[#8f100b] px-4 py-2 text-white hover:bg-[#7a0e0a]"
                                                >
                                                    Add to cart
                                                </button>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {canEditSales && <aside className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm h-fit xl:sticky xl:top-6">
                    <h2 className="text-2xl font-bold text-[#e11d2e] mb-4">Your requests</h2>

                    <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
                        {cart.length === 0 && <p className="text-sm text-gray-500">Cart is empty</p>}

                        {cart.map((item) => (
                            <div key={item.productId} className="border-b border-gray-200 pb-4">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-3">
                                        <div className="h-12 w-12 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                                            {item.imageUrl ? (
                                                <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-cover" />
                                            ) : (
                                                <span className="text-xs text-gray-400">No img</span>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-semibold">{item.productName}</p>
                                            <p className="text-[#e11d2e] font-bold">${item.unitPrice.toFixed(2)} each</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeCartItem(item.productId)}
                                        className="h-8 w-8 rounded-md bg-red-500 text-white"
                                    >
                                        x
                                    </button>
                                </div>

                                <div className="mt-3 flex items-center justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => updateCartQty(item.productId, item.quantity - 1)}
                                        className="h-8 w-8 rounded-md bg-amber-500 text-black font-bold"
                                    >
                                        -
                                    </button>
                                    <div className="w-8 text-center font-semibold">{item.quantity}</div>
                                    <button
                                        type="button"
                                        onClick={() => updateCartQty(item.productId, item.quantity + 1)}
                                        className="h-8 w-8 rounded-md bg-amber-500 text-black font-bold"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 border-t pt-4">
                        <p className="text-3xl font-bold text-[#e11d2e]">${total.toFixed(2)} Total</p>
                        <button
                            type="button"
                            onClick={checkout}
                            disabled={!cart.length || checkoutLoading}
                            className="mt-4 w-full rounded-md bg-black px-4 py-3 text-white disabled:opacity-50"
                        >
                            {checkoutLoading ? "Checking out..." : "Checkout"}
                        </button>
                    </div>
                </aside>}
            </div>
        </div>
    );
};

export default SalesPage;
